import { api } from '../../../../components/common';
import { phoneForApi, phoneFromApi, emailForApi, emailFromApi } from '../../../../utils/validation';
import { PaginatedResponse } from '../../../../types';
import {
  Contact,
  ContactWithDetails,
  Phone,
  Email,
  Address,
  ContactsAddresses,
  CreateContactsAddressRequest
} from '../types';

// Helper function to convert server verified (number 0/1) to client boolean
const convertVerifiedToBoolean = (verified: any): boolean => {
  if (verified === undefined || verified === null) return false;
  return verified === 1 || verified === '1' || verified === true || (typeof verified === 'number' && verified > 0);
};

const convertSqliteFlagToBoolean = (value: any): boolean =>
  value === 1 || value === '1' || value === true;

const convertIncludeInExportToBoolean = convertSqliteFlagToBoolean;
const convertIsPreferredToBoolean = convertSqliteFlagToBoolean;

// Contacts
export const contactsApi = {
  getAll: (params: any = {}): Promise<PaginatedResponse<ContactWithDetails>> => {
    // Convert params to URLSearchParams if it's a plain object
    let queryString = '';
    if (params instanceof URLSearchParams) {
      queryString = params.toString();
    } else if (params && typeof params === 'object') {
      const urlParams = new URLSearchParams();
      Object.keys(params).forEach(key => {
        const value = params[key];
        if (value !== undefined && value !== null) {
          if (Array.isArray(value) || typeof value === 'object') {
            urlParams.append(key, JSON.stringify(value));
          } else {
            urlParams.append(key, String(value));
          }
        }
      });
      queryString = urlParams.toString();
    }
    return api.get(`/contacts?${queryString}`).then(res => ({
      ...res.data,
      data: res.data.data.map((contact: ContactWithDetails) => ({
        ...contact,
        includeInExport: convertIncludeInExportToBoolean(contact.includeInExport),
        isPreferred: convertIsPreferredToBoolean(contact.isPreferred),
        phones: (contact.phones || []).map(phoneFromApi),
        emails: (contact.emails || []).map(emailFromApi),
        addresses: (contact.addresses || []), // Keep legacy addresses for backward compatibility
      contactsAddresses: (contact.contactsAddresses || []).map((ca: any) => ({
        ...ca,
        verified: convertVerifiedToBoolean(ca.verified),
        isPrimary: ca.isPrimary === 1 || ca.isPrimary === true,
        address: ca.address ? {
          ...ca.address,
          verified: convertVerifiedToBoolean(ca.address.verified)
        } : undefined
      })),
        primaryPhone: contact.primaryPhone ? phoneFromApi(contact.primaryPhone) : undefined,
        primaryEmail: contact.primaryEmail ? emailFromApi(contact.primaryEmail) : undefined,
        primaryAddress: contact.primaryAddress // Keep legacy primary address for backward compatibility
      }))
    }));
  },
    
  getById: (id: number): Promise<ContactWithDetails> =>
    api.get(`/contacts/${id}`).then(res => ({
      ...res.data,
      includeInExport: convertIncludeInExportToBoolean(res.data.includeInExport),
      isPreferred: convertIsPreferredToBoolean(res.data.isPreferred),
      phones: (res.data.phones || []).map(phoneFromApi),
      emails: (res.data.emails || []).map(emailFromApi),
      addresses: (res.data.addresses || []), // Keep legacy addresses for backward compatibility
      contactsAddresses: (res.data.contactsAddresses || []).map((ca: any) => ({
        ...ca,
        verified: convertVerifiedToBoolean(ca.verified),
        isPrimary: ca.isPrimary === 1 || ca.isPrimary === true,
        address: ca.address ? {
          ...ca.address,
          verified: convertVerifiedToBoolean(ca.address.verified)
        } : undefined
      })),
      primaryPhone: res.data.primaryPhone ? phoneFromApi(res.data.primaryPhone) : undefined,
      primaryEmail: res.data.primaryEmail ? emailFromApi(res.data.primaryEmail) : undefined,
      primaryAddress: res.data.primaryAddress // Keep legacy primary address for backward compatibility
    })),
    
  create: (contact: Omit<Contact, 'id' | 'dateCreated' | 'dateUpdated' | 'whoUpdated'> & { enableAccess?: boolean }): Promise<Contact> =>
    api.post('/contacts', contact).then(res => res.data),
    
  update: (id: number, contact: Partial<Contact> & { enableAccess?: boolean }): Promise<Contact> =>
    api.put(`/contacts/${id}`, contact).then(res => res.data),
    
  delete: (id: number): Promise<void> =>
    api.delete(`/contacts/${id}`).then(() => {}),
    
  enableAccess: (id: number): Promise<void> =>
    api.post(`/contacts/${id}/enable-access`).then(() => {}),
    
  disableAccess: (id: number): Promise<void> =>
    api.post(`/contacts/${id}/disable-access`).then(() => {}),
    
  checkAccessEnabled: (id: number): Promise<boolean> =>
    api.get(`/contacts/${id}/check-access`).then(res => res.data.enabled),

  importCsv: (file: File): Promise<{ processed: number; notProcessed: Array<{ row: number; data: string; reason: string }> }> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/contacts/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    }).then(res => res.data);
  },

  exportCsv: (params: Record<string, string | number | undefined>): Promise<Blob> => {
    const urlParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        urlParams.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      }
    });
    return api.get(`/contacts/export?${urlParams.toString()}`, { responseType: 'blob' })
      .then(res => res.data);
  },
};

// Phones
export const phonesApi = {
  getByContactId: (contactId: number): Promise<Phone[]> =>
    api.get(`/contacts/${contactId}/phones`).then(res => res.data.map(phoneFromApi)),
    
  create: (contactId: number, phone: Omit<Phone, 'id' | 'contactId'>): Promise<Phone> =>
    api.post(`/contacts/${contactId}/phones`, phoneForApi(phone)).then(res => phoneFromApi(res.data)),
    
  update: (id: number, phone: Partial<Phone>): Promise<Phone> => {
    const phoneData = phone.id ? phone : { ...phone, id: 0 }; // Ensure id exists for type safety
    return api.put(`/phones/${id}`, phoneForApi(phoneData as Phone)).then(res => phoneFromApi(res.data));
  },
    
  delete: (id: number): Promise<void> =>
    api.delete(`/phones/${id}`).then(() => {}),
};

// Emails
export const emailsApi = {
  getByContactId: (contactId: number): Promise<Email[]> =>
    api.get(`/contacts/${contactId}/emails`).then(res => res.data.map(emailFromApi)),
    
  create: (contactId: number, email: Omit<Email, 'id' | 'contactId'>): Promise<Email> =>
    api.post(`/contacts/${contactId}/emails`, emailForApi(email)).then(res => emailFromApi(res.data)),
    
  update: (id: number, email: Partial<Email>): Promise<Email> => {
    const emailData = email.id ? email : { ...email, id: 0 }; // Ensure id exists for type safety
    return api.put(`/emails/${id}`, emailForApi(emailData as Email)).then(res => emailFromApi(res.data));
  },
    
  delete: (id: number): Promise<void> =>
    api.delete(`/emails/${id}`).then(() => {}),
};

// ContactsAddresses - New API for address management
export const contactsAddressesApi = {
  getByContactId: (contactId: number): Promise<ContactsAddresses[]> =>
    api.get(`/contacts/${contactId}/addresses`).then(res => 
      (res.data || []).map((ca: any) => ({
        ...ca,
        verified: convertVerifiedToBoolean(ca.verified),
        isPrimary: ca.isPrimary === 1 || ca.isPrimary === true,
        address: ca.address ? {
          ...ca.address,
          verified: convertVerifiedToBoolean(ca.address.verified)
        } : undefined
      }))
    ),
    
  create: (data: CreateContactsAddressRequest): Promise<ContactsAddresses> =>
    api.post('/contacts-addresses', data).then(res => res.data),
    
  createAssociation: (data: Omit<CreateContactsAddressRequest, 'address'>): Promise<ContactsAddresses> =>
    api.post('/contacts-addresses/association', data).then(res => res.data),
    
  updateAddress: (id: number, address: any): Promise<ContactsAddresses> =>
    api.put(`/contacts-addresses/${id}/address`, { address }).then(res => res.data),
    
  updateDescription: (id: number, description: string): Promise<ContactsAddresses> =>
    api.put(`/contacts-addresses/${id}/description`, { description }).then(res => res.data),
    
  updateType: (id: number, typeId: number): Promise<ContactsAddresses> =>
    api.put(`/contacts-addresses/${id}/type`, { typeId }).then(res => res.data),
    
  setPrimary: (id: number): Promise<ContactsAddresses> =>
    api.put(`/contacts-addresses/${id}/primary`).then(res => res.data),
    
  delete: (id: number): Promise<void> =>
    api.delete(`/contacts-addresses/${id}`).then(() => {}),
};

// Legacy Addresses API - Keep for backward compatibility during transition
export const addressesApi = {
  getByContactId: (contactId: number): Promise<Address[]> =>
    api.get(`/contacts/${contactId}/addresses`).then(res => res.data),
    
  create: (contactId: number, address: Omit<Address, 'id' | 'contactId'>): Promise<Address> =>
    api.post(`/contacts/${contactId}/addresses`, address).then(res => res.data),
    
  update: (id: number, address: Partial<Address>): Promise<Address> => {
    const addressData = address.id ? address : { ...address, id: 0 }; // Ensure id exists for type safety
    return api.put(`/addresses/${id}`, addressData as Address).then(res => res.data);
  },
    
  delete: (id: number): Promise<void> =>
    api.delete(`/addresses/${id}`).then(() => {}),
};

